import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useActiveContextReady } from '@/hooks/useActiveContextReady';
import { useTagDefinitions, type TagDefinition } from '@/hooks/useTagDefinitions';
import { fetchTagValuesByDefinition, type TagValue } from '@/hooks/useTagValues';

/**
 * The decisions behind a tag selection, with no rendering attached.
 *
 * Split from `TagPicker.tsx` because these are what other modules reach for: a rule
 * renders as a sentence built by a pure, directly-tested string function, which cannot
 * call a hook. Keeping them beside the component would also make every consumer import
 * a component to get a formatter.
 */

/** What the picker yields. Mirrors the clause and resource-tag shapes both callers store. */
export interface TagSelection {
  tagDefinitionId: string;
  /** See the note above: "any value" or "not yet chosen", depending on `valueMode`. */
  tagValueId: string | null;
  /** Only ever set when the caller opted into freeform text. */
  freeformValue?: string | null;
}

/** A tag value's display text, preferring the human name over the stored token. */
export function tagValueLabel(value: TagValue): string {
  return value.displayName ?? value.value;
}

/**
 * Which definitions a caller may pick from.
 *
 * Declared here with its own type rather than borrowing the component's props: the
 * filtering is the part other surfaces reuse without rendering a picker at all, and
 * pointing it at `TagPickerProps` would make every such caller depend on a component.
 */
export interface TagDefinitionFilter {
  organizationId: string | null | undefined;
  /** Keep definitions whose `validFor` covers **any** of these. Omit for all. */
  resourceTypes?: string[];
  excludeDefinitionIds?: string[];
  filter?: (definition: TagDefinition) => boolean;
}

export function useSelectableTagDefinitions({
  organizationId,
  resourceTypes,
  excludeDefinitionIds,
  filter,
}: TagDefinitionFilter) {
  const { data, isLoading } = useTagDefinitions(organizationId);

  const definitions = useMemo(() => {
    const excluded = new Set(excludeDefinitionIds ?? []);
    return (data ?? []).filter((definition) => {
      if (excluded.has(definition.id)) return false;
      if (resourceTypes && !definition.validFor.some((t) => resourceTypes.includes(t))) return false;
      return filter ? filter(definition) : true;
    });
  }, [data, resourceTypes, excludeDefinitionIds, filter]);

  return { definitions, isLoading };
}

/**
 * Value-id → label, for however many definitions are on screen.
 *
 * Sentences are built by pure functions that cannot call a hook, so the labels have
 * to arrive as data. `useQueries` over exactly the definitions in use keeps that to
 * one request per tag actually referenced — usually none — rather than one per tag
 * the organization has ever defined. Same key as `useTagValuesByDefinition`, so a
 * picker opened afterwards reads the cache instead of refetching.
 */
export function useTagValueLabels(
  organizationId: string | null | undefined,
  tagDefinitionIds: string[],
): Record<string, string> {
  const contextReady = useActiveContextReady();
  const ids = useMemo(() => [...new Set(tagDefinitionIds)].sort(), [tagDefinitionIds]);

  // Folded in `combine` rather than a `useMemo` over the results array: `useQueries`
  // returns a fresh array every render, so a memo keyed on it would recompute anyway
  // while claiming not to.
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: ['tagValues', organizationId, 'definition', id],
      queryFn: () => fetchTagValuesByDefinition(id),
      enabled: !!organizationId && contextReady,
    })),
    combine: (results) => {
      const labels: Record<string, string> = {};
      for (const result of results) {
        for (const value of result.data ?? []) labels[value.id] = tagValueLabel(value);
      }
      return labels;
    },
  });
}
